import { Box, Avatar, Tooltip, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import { useSelector } from "react-redux";
import { selectRole } from "../../../../redux/features/user/selectors/userState.selectors";
import { colors } from "../../../ui/colors";
import { useContactCenterTheme } from "../../../virtual-receptionist/theming/ContactCenterThemeContext";

const PHONE_SENDER_TYPES = ["companyNumber", "contact"];

export const MessageContainer = ({ isOwn, message, children, senderDisplayName, statusSlot, bubbleTooltip, bubbleAccent }) => {
  const currentRole = useSelector(selectRole);
  const { theme } = useContactCenterTheme();
  const bubble = isOwn ? theme.bubbleOut : theme.bubbleIn;
  const senderName =
    senderDisplayName ??
    (message.sender?.businessName ?? `${message?.sender?.firstName} ${message?.sender?.lastName}`);
  const rawSenderRole =
    message.sender?.role?.type ?? message.sender?.roleType ?? message.sender?.workforceRole ?? (isOwn ? currentRole : message.sender?.type);
  const senderRole = PHONE_SENDER_TYPES.includes(rawSenderRole) ? null : rawSenderRole;
  const createdAt = message.createdAt ? new Date(message.createdAt) : null;
  const relativeTime = createdAt ? formatDistanceToNow(createdAt, { addSuffix: true, locale: enUS }) : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isOwn ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: 1.5,
        mb: 2.5,
        px: 1,

        width: { xs: "100%", md: "100%" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.35 }}>
        <Avatar
          src={message.sender?.avatar || ""}
          alt={senderName}
          sx={{
            width: 36,
            height: 36,
            bgcolor: isOwn ? theme.shell.accent : colors.gray[200],
            color: isOwn ? "#fff" : colors.gray[700],
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: `0 0 0 2px ${bubble.border}`,
          }}
        >
          {senderName[0]?.toUpperCase?.() || "?"}
        </Avatar>
        {senderRole ? (
          <Typography
            variant="caption"
            sx={{
              maxWidth: 56,
              fontSize: "0.58rem",
              lineHeight: 1,
              color: theme.shell.textSecondary,
              textAlign: "center",
              textTransform: "capitalize",
            }}
          >
            {senderRole}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "70%",
          alignItems: isOwn ? "flex-end" : "flex-start",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: theme.shell.textSecondary,
          }}
        >
          {senderName}
        </Typography>

        <Tooltip
          title={bubbleTooltip || ""}
          placement={isOwn ? "left" : "right"}
          enterDelay={200}
          slotProps={{ popper: { sx: { zIndex: "2147483647 !important" } } }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 3,
              bgcolor: bubble.bg,
              color: bubble.color,
              border: `1px solid ${bubble.border}`,
              borderLeft: bubbleAccent ? `3px solid ${bubbleAccent}` : `1px solid ${bubble.border}`,
              boxShadow: "none",
              maxWidth: { xs: "13rem", md: "100%" },
            }}
          >
            {children}

            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} mt={0.5}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  opacity: 0.7,
                  color: bubble.color,
                }}
              >
                {relativeTime}
              </Typography>
              {statusSlot}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};
